import '@pqina/pintura/pintura.css'

import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { MUIButton } from 'components/common/MUIButton'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import Stack from '@mui/material/Stack'
import { useClientPhotoStudio } from './ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

/** interface @Props for the VisualPinturaEditingFooter component. */
interface Props {
  /** onClose callback */
  onClose: () => void
  /** onSave callback */
  onSave: (tag: string) => void
  /** Whether the Photo Studio is processing image */
  isProcessing: boolean
  /** Whether the Photo Studio is saving image */
  isSaving?: boolean
  /** Whether the Photo Studio is switching tools */
  isToolSwitching?: boolean
  /** Whether save buttons should be enabled */
  canSave?: boolean
  /** Margin right for the footer */
  marginRight?: number
  /** Whether this footer is in a modal context (tool switching) */
  isModalContext?: boolean
  /** onStartFresh callback for modal context */
  onStartFresh?: () => void
}

export enum PhotoStudioSave {
  PHOTO_STUDIO_RE_EDIT_VISUAL = 'PHOTO_STUDIO_RE_EDIT_VISUAL',
  PHOTO_STUDIO_EDIT_VISUAL = 'PHOTO_STUDIO_EDIT_VISUAL',
}

/**
 * Client Photo Studio footer component for handling save and close actions.
 * @example <ClientPhotoStudioFooter/>
 */
export const ClientPhotoStudioFooter: React.FC<Props> = ({
  onClose,
  onSave,
  isProcessing,
  isSaving = false,
  isToolSwitching = false,
  canSave = false,
  marginRight = 0,
  isModalContext = false,
  onStartFresh
}) => {
  const { t } = useTranslation(['photo_studio_editing'])

  const {
    pinturaHistory,
    tag,
    setTag,
    isFileVersioned,
    selectedPinturaFeature,
    adjustHasEdits,
  } = useClientPhotoStudio()

  const isPinturaHistoryEmpty = !pinturaHistory || pinturaHistory.length === 0
  const isManualToolSelected = !!selectedPinturaFeature
  const isBetaSelfEditingCard = useFeatureFlag(FeatureFlag.BETA_SELF_EDIT_WIDGET)
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  // Check if there are any changes from any flow (AI flows or Pintura)
  // If canSave is true (AI flow changes exist), allow saving even if Pintura history is empty
  // If in Pintura and Pintura has changes, allow saving even if canSave is false
  // The in-house Adjust editor (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT) reports its own unsaved edits
  const hasAnyChanges = canSave || !isPinturaHistoryEmpty || adjustHasEdits
  const saveButtonDisabled = isProcessing ||
    isSaving ||
    isToolSwitching ||
    !hasAnyChanges ||
    (!hasWorkspaceActiveSubscription && !isBetaSelfEditingCard)

  return (
    <Stack
      gap={2}
      height="100%"
      marginRight={marginRight}
      alignItems="center"
      flexDirection="row"
      justifyContent="flex-end"
    >

      <MUIButton
        type="secondaryNoBorder"
        onClick={onClose}
        disabled={isProcessing || isSaving || isToolSwitching}
      >
        {t('cancel')}
      </MUIButton>

      {isModalContext && onStartFresh && (
        <MUIButton
          type="secondaryNoBorder"
          onClick={onStartFresh}
          disabled={isProcessing || isSaving || isToolSwitching}
        >
          {t('start_fresh')}
        </MUIButton>
      )}

      {isFileVersioned &&
        <MUIButton
          type="secondaryNoBorder"
          onClick={() => {
            onSave(PhotoStudioSave.PHOTO_STUDIO_RE_EDIT_VISUAL)
            setTag(PhotoStudioSave.PHOTO_STUDIO_RE_EDIT_VISUAL)
          }}
          disabled={isProcessing ||
            isSaving || isToolSwitching || !hasAnyChanges ||
            (!hasWorkspaceActiveSubscription && !isBetaSelfEditingCard)
          }
          isLoading={isSaving && tag === PhotoStudioSave.PHOTO_STUDIO_RE_EDIT_VISUAL}
        >
          {t('save_and_replace')}
        </MUIButton>
      }

      <MUIButton
        type="defaultPrimary"
        variant="outlined"
        onClick={() => {
          onSave(PhotoStudioSave.PHOTO_STUDIO_EDIT_VISUAL)
          setTag(PhotoStudioSave.PHOTO_STUDIO_EDIT_VISUAL)
        }}
        disabled={saveButtonDisabled}
        isLoading={isSaving && tag === PhotoStudioSave.PHOTO_STUDIO_EDIT_VISUAL}
        startIcon={<SaveOutlinedIcon fontSize='medium' />}
      >
        {t(isFileVersioned ? 'save_as_new' : 'save')}
      </MUIButton>

    </Stack>
  )
}
